import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMediaQuery, useTheme } from '@mui/material';

import { useAppDispatch } from '../../hooks';
import { toastReducer } from '../../store/reducers/toast.reducer';
import { SimpleScene } from '../model-preview/SimpleScene.tsx';
import { DXFData } from '../new-project/dxf/types';
import { CadBottomToolbar } from './CadBottomToolbar';
import { CadCanvas } from './CadCanvas';
import { CadCheatSheet } from './CadCheatSheet';
import { CadLeftToolbar } from './CadLeftToolbar';
import { CadPolygonSidebar, MOBILE_SHEET_COLLAPSED } from './CadPolygonSidebar';
import { CadScaleBar } from './CadScaleBar';
import { CadScaleDialog } from './CadScaleDialog';
import { CadSetupPanel } from './CadSetupPanel';
import { CadImportError, importCadFile, importCadSource, isCadFile } from './cad-import/cadImportClient';
import { CadImport, ImportedLayer, importParsedDrawing } from './cad-import/importCad';
import { findOverlappingPolygon } from './cadUtils';
import { DrawingCad, LoadedDrawing } from './drawingDocument';
import { parseSVG, simplifyPolygon } from './parseSVG';
import { CadPlan, NewPolygon, useCadState } from './useCadState';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'bmp', 'gif'];

/** Vertices closer than this to a straight run are dropped (2 cm: below drawing precision). */
const SIMPLIFY_TOLERANCE_M = 0.02;

/** Fewer vertices, never fewer than a polygon needs: a ring that would collapse stays as it is. */
function simplifyRing(ring: [number, number][], tolerance: number): [number, number][] {
  const simplified = simplifyPolygon(ring, tolerance);
  return simplified.length >= 3 ? simplified : ring;
}

/**
 * The editor on a saved drawing (Drawings tab) instead of the new-project flow. Polygons get a name,
 * a type and a height; lighting is set up later in a project's workspace.
 */
interface CadEditMode {
  /** The saved plan, or null for a drawing not drawn yet (it starts with the upload step). */
  drawing: LoadedDrawing | null;
  /** The drawing's plan image, shown where it was placed. */
  referenceImageUrl: string | null;
  /** Receives the plan after every change (the caller autosaves it). */
  onChange: (plan: EditedPlan) => void;
  /** A plan image was imported; the caller stores it with the drawing. */
  onImageImported: (file: File) => void;
  /** A DWG/DXF was imported; the caller stores `dxf` (a DWG converted) with the drawing. */
  onCadImported?: (dxf: Blob) => void;
  /** The stored CAD file of the drawing (`drawing.cad`), read again to show its lines and layers. */
  cadSourceUrl?: string | null;
  /** Leaving the upload step without a file. */
  onCancel: () => void;
  /** A new drawing that skips the upload step: a blank canvas, or a plan file to import right away. */
  startWith?: { draw: true } | { file: File } | null;
  /** Sidebar header and footer in place of the project name and the create buttons. */
  header: ReactNode;
  footer: ReactNode;
}

/** The plan as the editor has it: what is saved, plus the imported CAD drawing's lines for pictures. */
export type EditedPlan = CadPlan & {
  imageScale: number | null;
  cad: DrawingCad | null;
  reference: DXFData | null;
};

export default function CadProjectWizard({ edit }: { edit: CadEditMode }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // File state
  const [cadImport, setCadImport] = useState<CadImport | null>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  /** Ids of the polygons created from each imported layer, so a layer is not created twice. */
  const [layerPolygonIds, setLayerPolygonIds] = useState<Record<string, string[]>>(edit.drawing?.cad?.layers ?? {});
  /** The imported DWG/DXF's name while its lines are shown (not for SVG, which is not stored). */
  const [cadFileName, setCadFileName] = useState<string | null>(null);
  const editImageScale = edit.drawing?.imageScale ?? null;
  const [imageUrl, setImageUrl] = useState<string | null>(editImageScale !== null ? edit.referenceImageUrl : null);
  const [imageScale, setImageScale] = useState(editImageScale ?? 0.05);
  const [hasFile, setHasFile] = useState(!!edit.drawing);

  // Editor state
  const {
    state,
    addPolygon,
    addPolygons,
    updatePolygon,
    updatePolygonSilent,
    pushUndoCommand,
    deletePolygon,
    updatePolygons,
    deletePolygons,
    subtractFromPolygon,
    splitFromPolygon,
    setTool,
    setViewMode,
    setDrawingPoints,
    setActivePolygon,
    addMeasurementPoint,
    clearMeasurement,
    addScalePoint,
    clearScalePoints,
    setScale,
    setScaleRatio,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useCadState(edit.drawing ?? undefined);

  // UI state
  const [showCheatSheet, setShowCheatSheet] = useState(false);
  const [coord, setCoord] = useState({ x: 0, y: 0 });
  const [highlightedLayer, setHighlightedLayer] = useState<string | null>(null);
  const [frustumWidth, setFrustumWidth] = useState(800);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const captureRef = useRef<(() => string) | null>(null);
  const drawingPointsRef = useRef(state.drawingPoints);
  drawingPointsRef.current = state.drawingPoints;

  // ── Multi-select ───────────────────────────────────────────────────────────
  // Polygons ticked in the list (or Shift/⌘-clicked on the plan) for a change to all of them. The
  // selection is for that one change: it clears as soon as the user turns to something else —
  // drawing, picking a single polygon, another tool — or presses Escape.
  const [selectedPolygonIds, setSelectedPolygonIds] = useState<Set<string>>(new Set());
  const clearSelection = useCallback(() => setSelectedPolygonIds((ids) => (ids.size ? new Set() : ids)), []);
  const togglePolygonSelected = useCallback((id: string) => {
    setSelectedPolygonIds((ids) => {
      const next = new Set(ids);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  /** Picking one polygon (a row, or a plain click on the plan) leaves the multi-selection. */
  const selectPolygon = useCallback(
    (index: number | null) => {
      clearSelection();
      setActivePolygon(index);
    },
    [clearSelection, setActivePolygon],
  );
  useEffect(() => {
    // Deleted polygons (undo, the plan, …) leave the selection.
    setSelectedPolygonIds((ids) => {
      if (ids.size === 0) return ids;
      const present = new Set(state.polygons.map((p) => p.id));
      const next = new Set([...ids].filter((id) => present.has(id)));
      return next.size === ids.size ? ids : next;
    });
  }, [state.polygons]);
  useEffect(() => {
    // Starting to draw, or a drawing tool, is something else.
    if (state.drawingPoints.length > 0) clearSelection();
  }, [state.drawingPoints.length, clearSelection]);
  useEffect(() => {
    if (state.tool !== 'select' && state.tool !== 'pan') clearSelection();
  }, [state.tool, clearSelection]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      if (e.key !== 'Escape' || el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA') return;
      clearSelection();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clearSelection]);

  // ── File loading handlers ──────────────────────────────────────────────────

  const handleDrawingImported = useCallback(
    (result: CadImport) => {
      setCadImport(result);
      setLayerPolygonIds({});
      setImageUrl(null);
      setHasFile(true);
      // The drawing's own unit (header, or the plausible one when the header is wrong).
      setScale(result.metersPerUnit, null);
    },
    [setScale],
  );

  const handleImageLoaded = useCallback(
    (url: string) => {
      setImageUrl(url);
      setCadImport(null);
      setHasFile(true);
      setTool('set-scale'); // auto-activate scale calibration
    },
    [setTool],
  );

  // A saved drawing traced from a DWG/DXF: its lines and layers come back (the created layers too).
  const restoreCad = useRef(
    edit.drawing?.cad && edit.cadSourceUrl ? { url: edit.cadSourceUrl, cad: edit.drawing.cad } : null,
  );
  useEffect(() => {
    // Read once per mount (StrictMode mounts twice; the first read is cancelled).
    const restore = restoreCad.current;
    if (!restore) return;
    let cancelled = false;
    setImporting(true);
    importCadSource(restore.url)
      .then((result) => {
        if (cancelled) return;
        setCadImport(result);
        setCadFileName(restore.cad.fileName);
      })
      .catch((err) => {
        console.error('Loading the drawing file failed:', err);
        if (!cancelled)
          dispatch(toastReducer.actions.showToast({ message: 'drawings.sourceLoadFailed', type: 'error' }));
      })
      .finally(() => !cancelled && setImporting(false));
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const handleSkipUpload = useCallback(() => {
    setHasFile(true);
    setScale(1, null); // no image = no calibration needed, default 1 unit = 1m
  }, [setScale]);

  /** Any supported file: DXF and DWG drawings, SVG (structured, else as an image) and images. */
  const handleImportFile = useCallback(
    async (file: File) => {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      setImportError(null);
      try {
        if (isCadFile(file)) {
          setImporting(true);
          const { result, dxf } = await importCadFile(file);
          if (result.stats.entities === 0) throw new CadImportError(t('projects.cadProject.noEntitiesFound'));
          handleDrawingImported(result);
          setCadFileName(file.name);
          edit.onCadImported?.(dxf);
        } else if (ext === 'svg') {
          let drawing: CadImport | null = null;
          try {
            const data = parseSVG(await file.text());
            if (data.entities.length > 0) drawing = importParsedDrawing(data);
          } catch (err) {
            console.error('SVG parse error:', err);
          }
          // Without shapes the SVG is still a picture of the plan.
          if (drawing) {
            handleDrawingImported(drawing);
            setCadFileName(null);
          } else handleImageLoaded(URL.createObjectURL(file));
        } else if (IMAGE_EXTENSIONS.includes(ext)) {
          handleImageLoaded(URL.createObjectURL(file));
          edit.onImageImported(file);
        } else {
          throw new CadImportError(t('projects.cadProject.unsupportedFormat'));
        }
      } catch (err) {
        console.error('Import failed:', err);
        const message = err instanceof CadImportError ? err.message : t('projects.cadProject.parseError');
        if (hasFile) dispatch(toastReducer.actions.showToast({ message, type: 'error' }));
        else setImportError(message);
      } finally {
        setImporting(false);
      }
    },
    [handleDrawingImported, handleImageLoaded, hasFile, dispatch, t, edit],
  );

  // ── Unsaved changes warning ──────────────────────────────────────────────
  // ── Vertex update handlers ───────────────────────────────────────────────
  const handleUpdatePolygonPoints = useCallback(
    (id: string, points: [number, number][]) => {
      updatePolygon(id, { points });
    },
    [updatePolygon],
  );

  /** Silent update during canvas drag — no undo push per frame. */
  const handleUpdatePolygonPointsSilent = useCallback(
    (id: string, points: [number, number][]) => {
      updatePolygonSilent(id, { points });
    },
    [updatePolygonSilent],
  );

  /** Push a single undo snapshot after a drag completes. */
  const handlePushUndoSnapshot = useCallback(
    (polygonId: string, oldPoints: [number, number][]) => {
      pushUndoCommand({ type: 'UPDATE_POLYGON', polygonId, oldValues: { points: oldPoints } });
    },
    [pushUndoCommand],
  );

  // ── Polygon handlers ───────────────────────────────────────────────────────

  const handlePolygonComplete = useCallback(
    (points: [number, number][], holes?: [number, number][][]) => {
      if (state.tool === 'subtract' || state.tool === 'subtract-circle') {
        const targetIdx = findOverlappingPolygon(state.polygons, points);
        if (targetIdx === -1) {
          dispatch(
            toastReducer.actions.showToast({ message: 'projects.cadProject.subtractNoOverlap', type: 'warning' }),
          );
          setDrawingPoints([]);
          return;
        }
        subtractFromPolygon(state.polygons[targetIdx].id, points);
      } else if (state.tool === 'split') {
        const targetIdx = findOverlappingPolygon(state.polygons, points);
        if (targetIdx === -1) {
          dispatch(
            toastReducer.actions.showToast({ message: 'projects.cadProject.subtractNoOverlap', type: 'warning' }),
          );
          setDrawingPoints([]);
          return;
        }
        splitFromPolygon(state.polygons[targetIdx].id, points);
      } else {
        addPolygon(points, holes);
      }
    },
    [addPolygon, subtractFromPolygon, splitFromPolygon, state.tool, state.polygons, dispatch, setDrawingPoints],
  );

  // ── Imported drawing layers ────────────────────────────────────────────────

  /** Layers whose polygons are (still) in the plan. */
  const createdLayers = useMemo(() => {
    const present = new Set(state.polygons.map((p) => p.id));
    return new Set(
      Object.entries(layerPolygonIds)
        .filter(([, ids]) => ids.some((id) => present.has(id)))
        .map(([layer]) => layer),
    );
  }, [layerPolygonIds, state.polygons]);

  /** Polygons of the given imported layers, typed as the layer was recognised; one undo step. */
  const createFromLayers = useCallback(
    (layers: ImportedLayer[]) => {
      const tolerance = SIMPLIFY_TOLERANCE_M / state.metersPerUnit;
      const created: Record<string, string[]> = {};
      const polygons: NewPolygon[] = [];
      for (const layer of layers) {
        if (createdLayers.has(layer.name)) continue;
        created[layer.name] = [];
        layer.polygons.forEach((polygon, index) => {
          const id = crypto.randomUUID();
          created[layer.name].push(id);
          polygons.push({
            id,
            name: layer.polygons.length > 1 ? `${layer.name} ${index + 1}` : layer.name,
            type: layer.cls.type,
            points: simplifyRing(polygon.points, tolerance),
            holes: polygon.holes?.map((hole) => simplifyRing(hole, tolerance)),
          });
        });
      }
      if (polygons.length === 0) return;
      addPolygons(polygons);
      setLayerPolygonIds((current) => ({ ...current, ...created }));
    },
    [addPolygons, createdLayers, state.metersPerUnit],
  );

  /** Every layer that was recognised as a surface (road, sidewalk, building, green) and is visible. */
  const handleCreateAllLayers = useCallback(() => {
    if (!cadImport) return;
    createFromLayers(
      cadImport.layers.filter((l) => !l.hidden && l.cls.type !== 'Unspecified' && l.polygons.length > 0),
    );
  }, [cadImport, createFromLayers]);

  const handleCreatePolygonsFromLayer = useCallback(
    (layerName: string) => {
      const layer = cadImport?.layers.find((l) => l.name === layerName);
      if (layer) createFromLayers([layer]);
    },
    [cadImport, createFromLayers],
  );

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  // Edit mode: the caller gets every change of the plan (polygons, calibration, image placement).
  const onPlanChange = edit.onChange;
  const hasImage = !!imageUrl;
  const cad = useMemo<DrawingCad | null>(
    () => (cadImport && cadFileName ? { fileName: cadFileName, layers: layerPolygonIds } : null),
    [cadImport, cadFileName, layerPolygonIds],
  );
  const reference = cadImport?.reference ?? null;
  useEffect(() => {
    onPlanChange?.({
      polygons: state.polygons,
      metersPerUnit: state.metersPerUnit,
      scaleRatio: state.scaleRatio,
      scaleSet: state.scaleSet,
      imageScale: hasImage ? imageScale : null,
      cad,
      reference,
    });
  }, [
    onPlanChange,
    state.polygons,
    state.metersPerUnit,
    state.scaleRatio,
    state.scaleSet,
    hasImage,
    imageScale,
    cad,
    reference,
  ]);

  // ── Keyboard shortcuts for undo/redo ─────────────────────────────────────
  useEffect(() => {
    if (!hasFile) return; // Only active in editor phase
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod) return;

      // Don't hijack undo/redo inside text inputs
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        // During drawing, CadCanvas already handles point undo — only call stack undo otherwise
        if (drawingPointsRef.current.length === 0) undo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasFile, undo, redo]);

  // A new drawing started from the Drawings page ("Start drawing" / "Import plan"): straight past the
  // upload step, once.
  const startedWith = useRef(false);
  useEffect(() => {
    const start = edit.startWith;
    if (!start || startedWith.current || hasFile) return;
    startedWith.current = true;
    if ('draw' in start) handleSkipUpload();
    else void handleImportFile(start.file);
  }, [edit.startWith, hasFile, handleSkipUpload, handleImportFile]);

  // Phase 1: Setup (no file loaded yet)
  if (!hasFile) {
    return (
      <div className="w-full h-[calc(100dvh-64px)] relative overflow-hidden bg-black">
        {/* Background grid placeholder */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: isMobile ? 0 : 455,
            bottom: 0,
            background: 'transparent',
            zIndex: 0,
          }}
        >
          <SimpleScene />
        </div>

        <div style={{ position: 'relative', zIndex: 1, height: '100%', pointerEvents: 'none' }}>
          <CadSetupPanel
            onFileSelected={handleImportFile}
            importing={importing}
            error={importError}
            onSkip={handleSkipUpload}
            drawing={{ title: t('drawings.newDrawingTitle'), onCancel: edit.onCancel }}
          />
        </div>
      </div>
    );
  }

  // Phase 2: Editor (file loaded)
  return (
    <div className="w-full h-[calc(100dvh-64px)] relative overflow-hidden bg-[#0d0d0d]">
      {importing && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/50 text-sm text-gray-200">
          {t('projects.cadProject.importing')}
        </div>
      )}

      {/* Canvas area */}
      <div
        ref={canvasContainerRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: isMobile ? 0 : 455,
          // On a phone the collapsed list sheet sits below the plan.
          bottom: isMobile ? MOBILE_SHEET_COLLAPSED : 0,
          zIndex: 0,
        }}
      >
        <CadCanvas
          dxfData={cadImport?.reference ?? null}
          imageUrl={imageUrl}
          imageScale={imageScale}
          polygons={state.polygons}
          activePolygonIndex={state.activePolygonIndex}
          drawingPoints={state.drawingPoints}
          tool={state.tool}
          viewMode={state.viewMode}
          snapEnabled={state.snapEnabled}
          gridSize={state.gridSize}
          highlightedLayer={highlightedLayer}
          onPolygonComplete={handlePolygonComplete}
          onDrawingPointsChange={setDrawingPoints}
          onCoordChange={setCoord}
          onPolygonSelect={selectPolygon}
          selectedPolygonIds={selectedPolygonIds}
          onPolygonToggleSelected={togglePolygonSelected}
          onUpdatePolygonPoints={handleUpdatePolygonPoints}
          onUpdatePolygonPointsSilent={handleUpdatePolygonPointsSilent}
          onPushUndoSnapshot={handlePushUndoSnapshot}
          captureRef={captureRef}
          onZoomChange={setFrustumWidth}
          metersPerUnit={state.metersPerUnit}
          measurementPoints={state.measurementPoints}
          onAddMeasurementPoint={addMeasurementPoint}
          onClearMeasurement={clearMeasurement}
          onImageScaleChange={setImageScale}
          scalePoints={state.scalePoints}
          scaleSet={state.scaleSet}
          onAddScalePoint={addScalePoint}
          onClearScalePoints={clearScalePoints}
        />

        {/* Split button — above bottom toolbar when split tool active with 3+ points */}
        {state.viewMode === '2d' && state.tool === 'split' && state.drawingPoints.length >= 3 && (
          <div className="absolute bottom-[82px] left-1/2 -translate-x-1/2 z-50">
            <button
              onClick={() => handlePolygonComplete(state.drawingPoints)}
              className="px-5 py-2.5 rounded-xl text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors shadow-lg"
            >
              Split now (or press Enter)
            </button>
          </div>
        )}

        {/* Bottom toolbar */}
        <CadBottomToolbar
          viewMode={state.viewMode}
          scaleSet={state.scaleSet}
          metersPerUnit={state.metersPerUnit}
          scaleRatio={state.scaleRatio}
          tool={state.tool}
          onViewModeChange={setViewMode}
          onSetScaleTool={() => setTool('set-scale')}
          onScaleRatioChange={(mpu, ratio) => setScaleRatio(mpu, ratio)}
          onImportFile={handleImportFile}
          onUndo={() => {
            if (state.drawingPoints.length > 0) {
              setDrawingPoints(state.drawingPoints.slice(0, -1));
            } else {
              undo();
            }
          }}
          onRedo={redo}
          canUndo={state.drawingPoints.length > 0 || canUndo}
          canRedo={canRedo}
        />

        {/* Coordinates — inline left of bottom toolbar, vertically centered */}
        {state.viewMode === '2d' && (
          <div className="absolute bottom-[30px] left-[calc(50%+180px)] mr-3 px-3 py-1.5 bg-[#0a0e14]/90 border border-white/10 rounded-lg text-[11px] font-mono text-gray-400 z-50 pointer-events-none whitespace-nowrap">
            X:<span className="text-[#d4ff33] ml-0.5">{coord.x.toFixed(2)}</span> Y:
            <span className="text-[#d4ff33] ml-0.5">{coord.y.toFixed(2)}</span>
            <span className="text-gray-400 ml-1.5">
              ({state.scaleRatio ?? `${parseFloat(state.metersPerUnit.toFixed(2))} m/u`})
            </span>
          </div>
        )}

        {/* Hint shown while drawing a polygon, once there are enough points to create one */}
        {state.viewMode === '2d' && state.tool === 'draw' && state.drawingPoints.length > 2 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-blue-400/30 rounded-xl text-lg text-blue-300 z-50 pointer-events-none whitespace-nowrap">
            {t('projects.cadProject.drawPolygonEnterHint')}
          </div>
        )}

        {/* Tool instruction hints */}
        {state.viewMode === '2d' && state.tool === 'circle' && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-blue-400/30 rounded-xl text-lg text-blue-300 z-50 pointer-events-none whitespace-nowrap">
            {t('projects.cadProject.circleHintCenter')}
          </div>
        )}
        {state.viewMode === '2d' && state.tool === 'roundabout' && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-blue-400/30 rounded-xl text-lg text-blue-300 z-50 pointer-events-none whitespace-nowrap">
            {state.drawingPoints.length === 0
              ? t('projects.cadProject.roundaboutHintCenter')
              : state.drawingPoints.length === 1
                ? t('projects.cadProject.roundaboutHintInner')
                : t('projects.cadProject.roundaboutHintOuter')}
          </div>
        )}
        {state.viewMode === '2d' && state.tool === 'split' && state.drawingPoints.length === 0 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-orange-400/30 rounded-xl text-lg text-orange-300 z-50 pointer-events-none whitespace-nowrap">
            Draw a cut shape over a polygon, then press Enter or click &quot;Split now&quot;
          </div>
        )}
        {state.viewMode === '2d' && state.tool === 'subtract' && state.drawingPoints.length === 0 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-orange-400/30 rounded-xl text-lg text-orange-300 z-50 pointer-events-none whitespace-nowrap">
            {t('projects.cadProject.subtractHint')}
          </div>
        )}
        {state.viewMode === '2d' && state.tool === 'subtract-circle' && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-orange-400/30 rounded-xl text-lg text-orange-300 z-50 pointer-events-none whitespace-nowrap">
            {t('projects.cadProject.subtractCircleHint')}
          </div>
        )}
        {/* Set Scale hints */}
        {state.viewMode === '2d' && state.tool === 'set-scale' && state.scalePoints.length === 0 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-red-400/30 rounded-xl text-lg text-red-300 z-50 pointer-events-none whitespace-nowrap">
            {t('projects.cadProject.setScaleHint1', 'Click to place the first reference point on a known dimension')}
          </div>
        )}
        {state.viewMode === '2d' && state.tool === 'set-scale' && state.scalePoints.length === 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-red-400/30 rounded-xl text-lg text-red-300 z-50 pointer-events-none whitespace-nowrap">
            {t('projects.cadProject.setScaleHint2', 'Click to place the second point')}
          </div>
        )}
        {/* Scale distance input dialog */}
        {state.tool === 'set-scale' && state.scalePoints.length === 2 && (
          <CadScaleDialog
            pixelDistance={Math.hypot(
              state.scalePoints[1][0] - state.scalePoints[0][0],
              state.scalePoints[1][1] - state.scalePoints[0][1],
            )}
            onConfirm={(realDistanceMeters) => {
              const pixelDist = Math.hypot(
                state.scalePoints[1][0] - state.scalePoints[0][0],
                state.scalePoints[1][1] - state.scalePoints[0][1],
              );
              const mpu = pixelDist > 0 ? realDistanceMeters / pixelDist : 1;
              setScale(mpu, null);
            }}
            onCancel={clearScalePoints}
          />
        )}

        {/* Scale bar */}
        {state.viewMode === '2d' && (
          <CadScaleBar
            frustumWidth={frustumWidth}
            canvasWidth={canvasContainerRef.current?.clientWidth ?? 800}
            metersPerUnit={state.metersPerUnit}
          />
        )}

        {/* 3D mode indicator */}
        {state.viewMode === '3d' && (
          <div className="absolute bottom-[82px] left-1/2 -translate-x-1/2 px-3 py-1.5 bg-[#0a0e14]/90 border border-green03 rounded-lg text-xs text-green02 z-50 pointer-events-none">
            {t('projects.cadProject.view3DHint', '3D View — drag: orbit, scroll: zoom')}
          </div>
        )}
      </div>

      {/* Left toolbar */}
      <CadLeftToolbar
        tool={state.tool}
        onToolChange={setTool}
        onCheatSheetToggle={() => setShowCheatSheet(!showCheatSheet)}
        scaleSet={state.scaleSet}
        hasImage={!!imageUrl}
      />

      {/* Right sidebar */}
      <div style={{ position: 'relative', zIndex: 1, height: '100%', pointerEvents: 'none' }}>
        <CadPolygonSidebar
          polygons={state.polygons}
          activePolygonIndex={state.activePolygonIndex}
          drawingPointsCount={state.drawingPoints.length}
          onUpdatePolygon={updatePolygon}
          onDeletePolygon={deletePolygon}
          onUpdatePolygons={updatePolygons}
          onDeletePolygons={deletePolygons}
          selectedIds={selectedPolygonIds}
          onSelectedIdsChange={setSelectedPolygonIds}
          onSelectPolygon={selectPolygon}
          importedLayers={cadImport?.layers ?? null}
          createdLayers={createdLayers}
          onCreatePolygonsFromLayer={handleCreatePolygonsFromLayer}
          onCreateAllLayers={handleCreateAllLayers}
          onLayerHover={setHighlightedLayer}
          header={edit.header}
          footer={edit.footer}
        />
      </div>

      {/* CheatSheet */}
      {showCheatSheet && <CadCheatSheet onClose={() => setShowCheatSheet(false)} />}
    </div>
  );
}
